import React, { useState, useEffect, useContext } from 'react'
import axios from 'axios'
import startCase from 'lodash/startCase'
import { Box, CircularProgress, Typography } from '@mui/material'
import { LocalizationContext } from '../../../shared/contexts'
import { formatTenantTime } from '../../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../../utils/timeSource'

interface CommandLogRow {
  id: number
  queued_order_id: number | null
  recipe_name: string | null
  command: number | null
  command_name: string | null
  command_step_name: string | null
  substep_number: number | null
  container_slot_number: number | null
  container_id: number | null
  product_name: string | null
  amount_dosed: string | null
  start_time: string | null
  duration_seconds: number | null
}

const CommandLogsSection = ({ basePath }: { basePath: string }) => {
  const [rows, setRows] = useState<CommandLogRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const localization = useContext(LocalizationContext)

  useEffect(() => {
    axios.get(`${basePath}/command_logs`)
      .then(({ data }) => {
        setRows(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [basePath])

  if (loading) {
    return <Box className='text-center py-3'><CircularProgress size={24} /></Box>
  }

  if (!rows || rows.length === 0) {
    return <Typography variant='body2' color='text.secondary'>No command logs recorded for this order yet.</Typography>
  }

  const grouped = new Map<string, CommandLogRow[]>()
  rows.forEach((row) => {
    const key = `${row.queued_order_id ?? 'unknown'}|${row.recipe_name ?? ''}`
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(row)
  })

  return (
    <Box>
      {Array.from(grouped.entries()).map(([key, groupRows]) => {
        return (
          <Box key={key} className='command-log-group'>
            <table className='table table-robis order-show-command-log-table'>
              <thead className='thead-robis'>
                <tr>
                  <th>Substep</th>
                  <th>Command</th>
                  <th>Step</th>
                  <th>Product</th>
                  <th>Container</th>
                  <th>Dosed</th>
                  <th>Started</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {groupRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.substep_number ?? '—'}</td>
                    <td>{row.command_name ? startCase(row.command_name) : row.command ?? '—'}</td>
                    <td>{row.command_step_name || '—'}</td>
                    <td>{row.product_name || '—'}</td>
                    <td>
                      {row.container_slot_number != null
                        ? `#${row.container_slot_number}${row.container_id ? ` (ID: ${row.container_id})` : ''}`
                        : '—'}
                    </td>
                    <td>{row.amount_dosed || '—'}</td>
                    <td title={timeSourceTitle(row.start_time, TIME_SOURCE.robot)}>
                      {formatTenantTime(row.start_time, localization) || '—'}
                    </td>
                    <td>{row.duration_seconds != null ? `${row.duration_seconds}s` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )
      })}
    </Box>
  )
}

export default CommandLogsSection
